import React from 'react'
import { X, ExternalLink } from 'lucide-react'
import Autocomplete from 'react-google-autocomplete'
import './styles.scss'

export default function StudentVisaModals({
  activeModal,
  setActiveModal,
  i20Profile,
  updateProfile,
  addressInput,
  setAddressInput,
  flightInput,
  setFlightInput
}) {
  if (!activeModal) return null

  return (
    <div className="modal-overlay" onClick={() => setActiveModal(null)}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>

        {activeModal === 'ADDRESS' && (
          <>
            <div className="modal-header">
              <h3>Mailing Address</h3>
              <button className="close-btn" onClick={() => setActiveModal(null)}><X size={20} /></button>
            </div>
            <div className="modal-body form-layout">
              <p className="desc">Where should we mail your physical I-20 form?</p>
              <div className="form-group">
                <label>Full Address</label>
                <Autocomplete
                  apiKey={import.meta.env.VITE_GOOGLE_PLACES_API_KEY}
                  options={{ types: ['address'] }}
                  onPlaceSelected={(place) => {
                    setAddressInput(place.formatted_address || place.name);
                  }}
                  onChange={(e) => setAddressInput(e.target.value)}
                  defaultValue={addressInput}
                  placeholder="Search for your address..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setActiveModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={() => updateProfile({ mailingAddress: addressInput })}>Save Address</button>
            </div>
          </>
        )}

        {activeModal === 'SEVIS' && (
          <>
            <div className="modal-header">
              <h3>SEVIS I-901 Fee</h3>
              <button className="close-btn" onClick={() => setActiveModal(null)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <p className="desc">You must pay the $350 SEVIS fee to the US Government before your interview. Keep the receipt.</p>
              <a href="https://www.fmjfee.com/" target="_blank" rel="noreferrer" className="link-text">Go to fmjfee.com <ExternalLink size={14} /></a>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setActiveModal(null)}>Close</button>
              {i20Profile.sevisStatus === 'PAID' ? (
                <button className="btn btn-danger" onClick={() => updateProfile({ sevisStatus: 'NOT_STARTED' })}>Unmark as Paid</button>
              ) : (
                <button className="btn btn-primary" onClick={() => updateProfile({ sevisStatus: 'PAID' })}>Mark as Paid</button>
              )}
            </div>
          </>
        )}

        {activeModal === 'DS160' && (
          <>
            <div className="modal-header">
              <h3>DS-160 Application</h3>
              <button className="close-btn" onClick={() => setActiveModal(null)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <p className="desc">The DS-160 is the official US Visa application. You must submit it online before scheduling your interview.</p>
              <a href="https://ceac.state.gov/genniv/" target="_blank" rel="noreferrer" className="link-text">Go to ceac.state.gov <ExternalLink size={14} /></a>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setActiveModal(null)}>Close</button>
              {i20Profile.ds160Status === 'SUBMITTED' ? (
                <button className="btn btn-danger" onClick={() => updateProfile({ ds160Status: 'NOT_STARTED' })}>Unmark as Submitted</button>
              ) : (
                <button className="btn btn-primary" onClick={() => updateProfile({ ds160Status: 'SUBMITTED' })}>Mark as Submitted</button>
              )}
            </div>
          </>
        )}

        {activeModal === 'INTERVIEW' && (
          <>
            <div className="modal-header">
              <h3>Embassy Interview</h3>
              <button className="close-btn" onClick={() => setActiveModal(null)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <p className="desc">Have you scheduled your US Embassy interview? If so, click below to confirm.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setActiveModal(null)}>Close</button>
              {i20Profile.visaAppointmentStatus === 'SCHEDULED' ? (
                <button className="btn btn-danger" onClick={() => updateProfile({ visaAppointmentStatus: 'NOT_SCHEDULED' })}>Unmark Scheduled</button>
              ) : (
                <button className="btn btn-primary" onClick={() => updateProfile({ visaAppointmentStatus: 'SCHEDULED' })}>Mark Scheduled</button>
              )}
            </div>
          </>
        )}

        {activeModal === 'VISA_DECISION' && (
          <>
            <div className="modal-header">
              <h3>Record Visa Decision</h3>
              <button className="close-btn" onClick={() => setActiveModal(null)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <p className="desc">What was the outcome of your US Embassy visa interview?</p>
            </div>
            <div className="modal-footer" style={{ flexDirection: 'column' }}>
              <button className="btn btn-primary btn-full" onClick={() => updateProfile({ visaDecisionStatus: 'APPROVED' })}>My Visa was Approved</button>
              <button className="btn btn-danger btn-full" onClick={() => updateProfile({ visaDecisionStatus: 'REJECTED' })}>My Visa was Rejected</button>
              <button className="btn btn-outline btn-full" onClick={() => setActiveModal(null)}>Cancel</button>
            </div>
          </>
        )}

        {activeModal === 'FLIGHT' && (
          <>
            <div className="modal-header">
              <h3>Flight Details</h3>
              <button className="close-btn" onClick={() => setActiveModal(null)}><X size={20} /></button>
            </div>
            <div className="modal-body form-layout">
              <p className="desc">Enter your flight details so we can track your arrival and coordinate pickup.</p>
              
              <div className="form-group">
                <label>Flight Number</label>
                <input type="text" placeholder="e.g. DL123" value={flightInput.number} onChange={e => setFlightInput({...flightInput, number: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Arrival Airport</label>
                <input type="text" placeholder="e.g. JFK" value={flightInput.airport} onChange={e => setFlightInput({...flightInput, airport: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Final Destination / City</label>
                <input type="text" placeholder="e.g. New York City" value={flightInput.dest} onChange={e => setFlightInput({...flightInput, dest: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Estimated Arrival Time</label>
                <input type="text" placeholder="e.g. Aug 15, 8:30 PM" value={flightInput.time} onChange={e => setFlightInput({...flightInput, time: e.target.value})} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setActiveModal(null)}>Cancel</button>
              <button 
                className="btn btn-primary" 
                onClick={() => updateProfile({ 
                  flightTicketStatus: 'BOOKED', 
                  flightNumber: flightInput.number, 
                  arrivalAirport: flightInput.airport, 
                  destination: flightInput.dest, 
                  flightTime: flightInput.time 
                })}
              >
                Save Flight
              </button>
            </div>
          </>
        )}

        {activeModal === 'PICKUP' && (
          <>
            <div className="modal-header">
              <h3>Airport Pickup</h3>
              <button className="close-btn" onClick={() => setActiveModal(null)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <p className="desc">Would you like our student volunteers or university shuttle to pick you up from the airport?</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setActiveModal(null)}>Close</button>
              {i20Profile.airportPickupRequested === 'REQUESTED' ? (
                <button className="btn btn-danger" onClick={() => updateProfile({ airportPickupRequested: 'NOT_REQUESTED' })}>Cancel Request</button>
              ) : (
                <button className="btn btn-primary" onClick={() => updateProfile({ airportPickupRequested: 'REQUESTED' })}>Request Pickup</button>
              )}
            </div>
          </>
        )}

        {activeModal === 'ACCOMMODATION' && (
          <>
            <div className="modal-header">
              <h3>Accommodation</h3>
              <button className="close-btn" onClick={() => setActiveModal(null)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <p className="desc">Have you finalized your housing arrangements for the semester, or do you need assistance finding a place to stay?</p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1.5rem' }}>
                {i20Profile.accommodationStatus === 'SECURED' ? (
                  <button className="btn btn-danger btn-full" style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', cursor: 'pointer', border: 'none', background: '#ef4444', color: 'white', fontWeight: '500' }} onClick={() => updateProfile({ accommodationStatus: 'NOT_SECURED' })}>Undo (Mark Not Secured)</button>
                ) : (
                  <button className="btn btn-primary btn-full" style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', cursor: 'pointer', border: 'none', background: '#2563eb', color: 'white', fontWeight: '500' }} onClick={() => updateProfile({ accommodationStatus: 'SECURED' })}>I Have Secured Housing</button>
                )}

                {i20Profile.accommodationStatus === 'NEEDS_HELP' ? (
                  <button className="btn btn-danger btn-full" style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', cursor: 'pointer', border: 'none', background: '#ef4444', color: 'white', fontWeight: '500' }} onClick={() => updateProfile({ accommodationStatus: 'NOT_SECURED' })}>Cancel Help Request</button>
                ) : (
                  i20Profile.accommodationStatus !== 'SECURED' && (
                    <button className="btn btn-warning btn-full" onClick={() => updateProfile({ accommodationStatus: 'NEEDS_HELP' })} style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', cursor: 'pointer', background: '#f59e0b', color: 'white', border: 'none', fontWeight: '500' }}>I Need Help Finding Housing</button>
                  )
                )}
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  )
}
